import React, { ComponentProps } from 'react';

import cx from 'classnames';
import { isNil } from 'ramda';
import ReactTooltip from 'react-tooltip';

import styles from './index.module.css';
import { ITooltipProps, TTooltipVariant } from './types';

export const VARIANTS = {
  DEFAULT: 'default',
  DARK: 'dark',
  CUSTOM: 'custom',
} as const;

const map: Record<
  TTooltipVariant,
  Pick<ITooltipProps, 'className' | 'effect' | 'border' | 'type' | 'arrowColor' | 'backgroundColor' | 'textColor'>
> = {
  [VARIANTS.DEFAULT]: {
    className: '__react_component_tooltip_default',
    effect: 'solid',
    border: false,
    type: 'light',
    arrowColor: 'var(--tooltip-bg-color)',
    backgroundColor: 'var(--tooltip-bg-color)',
    textColor: 'var(--base-font_color)',
  },
  [VARIANTS.DARK]: {
    className: '__react_component_tooltip_default',
    effect: 'solid',
    border: false,
    type: 'dark',
    arrowColor: '#424242',
    backgroundColor: '#424242',
    textColor: 'var(--tooltip-bg-color)',
  },
  [VARIANTS.CUSTOM]: {
    className: '__react_component_tooltip_custom',
    effect: 'solid',
    border: true,
    type: 'warning',
    arrowColor: 'var(--tooltip-bg-color)',
    backgroundColor: 'var(--tooltip-bg-color)',
    textColor: 'var(--base-font_color)',
  },
};

function Tooltip({
  children,
  className,
  disable = false,
  place = 'top',
  variant = VARIANTS.DEFAULT,
  withMouseOver = false,
  ...props
}: ITooltipProps) {
  if (isNil(children) || disable) return null;

  // allows user to place mouse over the tooltip
  const mouseOverProps = withMouseOver
    ? {
        delayHide: 300,
        delayShow: 200,
        delayUpdate: 300,
      }
    : {};

  return (
    <ReactTooltip
      place={place}
      {...map[variant]}
      {...mouseOverProps}
      {...props}
      className={cx(map[variant].className, className, 'custom-tooltip')}
    >
      {typeof children === 'string' ? <TooltipText>{children}</TooltipText> : children}
    </ReactTooltip>
  );
}

export default Tooltip;

Tooltip.VARIANTS = VARIANTS;

export function TooltipText({ className, children, ...props }: ComponentProps<'div'>) {
  return (
    <div className={cx(styles.tooltipText, className, 'custom-tooltip-text')} {...props}>
      {children}
    </div>
  );
}
